import React from 'react'
import { createContext } from 'lib/createContext'
import { useDayforceJobQuery } from 'queries/admin/dayforce/useDayforceJobQuery'
import { useShiftDetailContextValue } from './ShiftDetailProvider'
import { useCreateWorkerSubmissionRequestMutation } from 'queries/admin/dayforce/useCreateWorkerSubmissionRequestMutation'
import { useScheduleWorkItemUpdateMutation } from 'queries/admin/dayforce/useScheduleWorkItemUpdateMutation'
import { useCreateContestedHoursRequestMutation } from 'queries/admin/dayforce/useCreateContestedHoursRequestMutation'
import { useCancelContestedHoursRequestMutation } from 'queries/admin/dayforce/useCancelContestedHoursRequestMutation'
import { parseErrorMessage } from 'api'

type DayforceJobContextState = {
  state: {
    job: NonNullable<ReturnType<typeof useDayforceJobQuery>>['data'] | undefined
    booking:
      | NonNullable<
          ReturnType<typeof useDayforceJobQuery>['data']
        >['bookings'][number]
      | undefined
    isPending: boolean
    isError: boolean
    error: ReturnType<typeof useDayforceJobQuery>['error'] | null
    scheduleWorkItemUpdateMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    workerSubmissionMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    contestedHoursRequestMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    cancelContestedHoursRequestMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    scheduleWorkItemUpdate: ({
      associationId,
      shiftId,
    }: {
      associationId: number
      shiftId: number
    }) => void
    createWorkerSubmission: ReturnType<
      typeof useCreateWorkerSubmissionRequestMutation
    >['mutate']
    createContestedHoursRequest: ReturnType<
      typeof useCreateContestedHoursRequestMutation
    >['mutate']
    cancelContestedHoursRequest: ReturnType<
      typeof useCancelContestedHoursRequestMutation
    >['mutate']
  }
}

const [Context, useDayforceJobContextValue] =
  createContext<DayforceJobContextState>({
    name: 'DayforceJobContext',
  })

export const DayforceJobProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    state: { dayforceAgencyId, dayforceJobId, dayforceBookingId },
  } = useShiftDetailContextValue()

  const {
    mutate: scheduleWorkItemUpdate,
    isPending: isScheduleWorkItemUpdatePending,
    isError: isScheduleWorkItemUpdateError,
    isSuccess: isScheduleWorkItemUpdateSuccess,
    error: scheduleWorkItemUpdateError,
  } = useScheduleWorkItemUpdateMutation()

  const {
    mutate: createWorkerSubmissionRequest,
    isPending: isCreateWorkerSubmissionRequestPending,
    isError: isCreateWorkerSubmissionRequestError,
    isSuccess: isCreateWorkerSubmissionRequestSuccess,
    error: createWorkerSubmissionRequestError,
  } = useCreateWorkerSubmissionRequestMutation()

  const {
    mutate: createContestedHoursRequest,
    isPending: isCreateContestedHoursRequestPending,
    isError: isCreateContestedHoursRequestError,
    isSuccess: isCreateContestedHoursRequestSuccess,
    error: createContestedHoursRequestError,
  } = useCreateContestedHoursRequestMutation()

  const {
    mutate: cancelContestedHoursRequest,
    isPending: isCancelContestedHoursRequestPending,
    isError: isCancelContestedHoursRequestError,
    isSuccess: isCancelContestedHoursRequestSuccess,
    error: cancelContestedHoursRequestError,
  } = useCancelContestedHoursRequestMutation()

  const { data, isPending, isError, error } = useDayforceJobQuery(
    dayforceAgencyId ?? '',
    dayforceJobId ?? ''
  )

  const booking = dayforceBookingId
    ? data?.bookings?.find((booking) => booking.bookingId === dayforceBookingId)
    : undefined

  const value = {
    state: {
      job: data,
      booking,
      isPending,
      isError,
      error: error ?? null,
      scheduleWorkItemUpdateMutationState: {
        isPending: isScheduleWorkItemUpdatePending,
        isError: isScheduleWorkItemUpdateError,
        isSuccess: isScheduleWorkItemUpdateSuccess,
        errorMessage: parseErrorMessage(scheduleWorkItemUpdateError),
      },
      workerSubmissionMutationState: {
        isPending: isCreateWorkerSubmissionRequestPending,
        isError: isCreateWorkerSubmissionRequestError,
        isSuccess: isCreateWorkerSubmissionRequestSuccess,
        errorMessage: parseErrorMessage(createWorkerSubmissionRequestError),
      },
      contestedHoursRequestMutationState: {
        isPending: isCreateContestedHoursRequestPending,
        isError: isCreateContestedHoursRequestError,
        isSuccess: isCreateContestedHoursRequestSuccess,
        errorMessage: parseErrorMessage(createContestedHoursRequestError),
      },
      cancelContestedHoursRequestMutationState: {
        isPending: isCancelContestedHoursRequestPending,
        isError: isCancelContestedHoursRequestError,
        isSuccess: isCancelContestedHoursRequestSuccess,
        errorMessage: parseErrorMessage(cancelContestedHoursRequestError),
      },
    },
    actions: {
      scheduleWorkItemUpdate,
      createWorkerSubmission: createWorkerSubmissionRequest,
      createContestedHoursRequest: createContestedHoursRequest,
      cancelContestedHoursRequest: cancelContestedHoursRequest,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceJobContextValue }
